import { ProfileAvatar } from "#profile-avatar";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Input } from "@-/dev.improve.haniverse.components.ui.input";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  getAuthClient,
  useAuthSession,
  type AuthSessionUser,
} from "@-/dev.improve.haniverse.lib.auth.client";
import { useAuthSheet } from "@-/dev.improve.haniverse.routes.nav.app.auth.sheet";
import { PostHogMaskView } from "@-/com.npmjs.posthog-react-native";
import { ActivityIndicator, View } from "@-/com.npmjs.react-native";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import ensureError from "@-/com.npmjs.ensure-error";
import { useState } from "react";

function ProfileEditor({ user }: { user: AuthSessionUser }) {
  const [name, setName] = useState(user.name);
  const [image, setImage] = useState(user.image ?? null);
  const [saved, setSaved] = useState(false);
  const photoMutation = useMutation({
    mutationFn: async () => {
      const { launchImageLibraryAsync } = await import("@-/com.npmjs.expo-image-picker");
      const result = await launchImageLibraryAsync({
        allowsEditing: true,
        aspect: [1, 1],
        mediaTypes: ["images"],
        quality: 1,
      });
      if (result.canceled) {
        return;
      }
      const asset = result.assets[0];
      if (!asset) {
        return;
      }
      const { manipulateAsync, SaveFormat } = await import("@-/com.npmjs.expo-image-manipulator");
      const size = Math.min(asset.width, asset.height);
      const photo = await manipulateAsync(
        asset.uri,
        [
          {
            crop: {
              height: size,
              originX: (asset.width - size) / 2,
              originY: (asset.height - size) / 2,
              width: size,
            },
          },
          { resize: { height: 128, width: 128 } },
        ],
        { base64: true, compress: 0.65, format: SaveFormat.JPEG },
      );
      if (!photo.base64) {
        throw new Error("Could not read this photo. Please choose another image.");
      }
      setImage(`data:image/jpeg;base64,${photo.base64}`);
      setSaved(false);
    },
    mutationKey: ["profilePhoto", user.id],
  });
  const saveMutation = useMutation({
    mutationFn: async () => {
      const result = await getAuthClient().updateUser({ image, name: name.trim() });
      if (result.error) {
        throw new Error(result.error.message ?? "Could not save your profile.");
      }
    },
    mutationKey: ["saveProfile", user.id],
    onSuccess: () => setSaved(true),
  });
  const busy = photoMutation.isPending || saveMutation.isPending;
  const error = photoMutation.error ?? saveMutation.error;
  return (
    <View className="gap-3">
      <Text className="text-lg font-semibold">Your profile</Text>
      <View className="flex-row items-center gap-3">
        <View className="size-14 overflow-hidden rounded-full">
          <ProfileAvatar user={{ ...user, image }} />
        </View>
        <View className="flex-1 gap-1">
          <Button
            disabled={busy}
            onPress={() => photoMutation.mutate()}
            size="sm"
            variant="outline"
          >
            <Text>{photoMutation.isPending ? "Opening photos…" : "Change photo"}</Text>
          </Button>
          {image ? (
            <Button
              disabled={busy}
              onPress={() => {
                setImage(null);
                setSaved(false);
              }}
              size="sm"
              variant="ghost"
            >
              <Text>Remove photo</Text>
            </Button>
          ) : null}
        </View>
      </View>
      <PostHogMaskView>
        <Text className="text-sm text-muted-foreground" numberOfLines={2}>
          {user.email}
        </Text>
      </PostHogMaskView>
      <View className="gap-1.5">
        <Text className="text-sm font-medium">Name</Text>
        <PostHogMaskView>
          <Input
            accessibilityLabel="Profile name"
            autoCapitalize="words"
            editable={!busy}
            maxLength={100}
            onChangeText={(value) => {
              setName(value);
              setSaved(false);
            }}
            value={name}
          />
        </PostHogMaskView>
      </View>
      {error ? (
        <Text accessibilityRole="alert" className="text-sm text-destructive">
          {ensureError(error).message}
        </Text>
      ) : null}
      {saved ? (
        <Text accessibilityLiveRegion="polite" className="text-sm text-muted-foreground">
          Profile saved
        </Text>
      ) : null}
      <Button
        disabled={
          busy ||
          name.trim().length === 0 ||
          (name.trim() === user.name && image === (user.image ?? null))
        }
        onPress={() => saveMutation.mutate()}
        size="sm"
      >
        <Text>{saveMutation.isPending ? "Saving…" : "Save changes"}</Text>
      </Button>
    </View>
  );
}

export function ProfileCard({ onNavigate }: { onNavigate?: () => void }) {
  const { isResolved, user } = useAuthSession();
  const { openAccount } = useAuthSheet();
  if (!isResolved) {
    return <ActivityIndicator accessibilityLabel="Loading profile" size="small" />;
  }
  if (!user) {
    return (
      <View className="gap-3">
        <Text className="text-lg font-semibold">Your profile</Text>
        <Text className="text-sm text-muted-foreground">
          Sign in to personalize your profile and sync your progress.
        </Text>
        <Button
          onPress={() => {
            onNavigate?.();
            openAccount({ source: "profile_card" });
          }}
          size="sm"
        >
          <Text>Sign in</Text>
        </Button>
      </View>
    );
  }
  return <ProfileEditor key={user.id} user={user} />;
}
